import { useState, useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiUrl, authFetch, authService, getImageUrl } from '../utils/api';

interface StudentDetails {
  id: string;
  userName: string;
  academicYear: string | null;
  email: string;
  phonenumber: string;
  parentPhone: string;
  imagePath: string | null;
  registrationDate: string;
  emailConfirmed: boolean;
  gradeName?: string | null;
  gradeDescription?: string | null;
  governorate?: string | null;
  center?: string | null;
}

interface EnrolledCourse {
  id: string;
  courseName: string;
  imagePath: string | null;
  description: string;
  enrollmentDate: string;
}

interface EnrolledCoursesResponse {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  courses: EnrolledCourse[];
}

interface EnrollCourseFormData {
  gradeId: string;
  term: string;
  courseId: string;
}

interface CourseOption {
  id: string;
  name: string;
  term: string;
  description: string;
  price: number;
}

const truncateText = (value: string, max: number) => {
  const v = (value ?? '').trim();
  if (v.length <= max) return v;
  return `${v.slice(0, Math.max(0, max - 1))}…`;
};

/** Max visible characters for enrolled course description (list + cards). */
const ENROLLED_COURSE_DESCRIPTION_MAX = 30;

interface SortConfig {
  key: keyof EnrolledCourse | null;
  direction: 'asc' | 'desc';
}

interface VideoProgressDto {
  totalVideos: number;
  watchedVideos: number;
  progress: number;
}

interface QuizResultDto {
  lessonId: number;
  lessonName: string;
  score: number;
  submittedAt: string;
}

interface QuizProgressDto {
  totalQuizzes: number;
  solvedQuizzes: number;
  results: QuizResultDto[];
}

/** Backend: /api/Course/all?pageNumber=1&pageSize=10&gradeId=<uuid>&active=true&term=First */
const buildCoursesUrl = (gradeId: string, term: string) => {
  const params = new URLSearchParams();
  params.set('pageNumber', '1');
  params.set('pageSize', '10');
  params.set('gradeId', gradeId);
  params.set('active', 'true');
  params.set('term', term);
  return apiUrl(`Course/all?${params.toString()}`);
};

interface GradeOption {
  id: string;
  name: string;
  code: string;
  level?: number;
  active?: boolean;
}

/** Map Grade API level to backend AcademicYear (GradeEnum) for AdminStudent/update */
const levelToAcademicYear: Record<number, string> = {
  1: 'Primary1', 2: 'Primary2', 3: 'Primary3', 4: 'Primary4', 5: 'Primary5', 6: 'Primary6',
  7: 'Prep1', 8: 'Prep2', 9: 'Prep3',
  10: 'Secondary1', 11: 'Secondary2', 12: 'Secondary3',
};

const apiAcademicYearToLevel: Record<string, number> = {
  Primary1: 1, Primary2: 2, Primary3: 3, Primary4: 4, Primary5: 5, Primary6: 6,
  Prep1: 7, Prep2: 8, Prep3: 9,
  Secondary1: 10, Secondary2: 11, Secondary3: 12,
};

const resolveGradeIdFromStudent = (
  studentData: StudentDetails,
  gradeList: GradeOption[]
): string => {
  if (studentData.gradeName) {
    const byName = gradeList.find(
      (g) => g.name.trim().toLowerCase() === studentData.gradeName!.trim().toLowerCase()
    );
    if (byName) return byName.id;
  }
  const year = studentData.academicYear?.replace(/\s+/g, '') ?? '';
  const level = apiAcademicYearToLevel[year];
  if (level != null) {
    const byLevel = gradeList.find((g) => g.level === level);
    if (byLevel) return byLevel.id;
  }
  return '';
};

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState<StudentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    gradeId: '',
    phonenumber: '',
    parentPhone: ''
  });
  const [grades, setGrades] = useState<GradeOption[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [gradesError, setGradesError] = useState<string | null>(null);
  const [updateMessage, setUpdateMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'courses' | 'progress'>('details');
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isUnenrolling, setIsUnenrolling] = useState(false);
  const [courseToUnenroll, setCourseToUnenroll] = useState<EnrolledCourse | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollFormData, setEnrollFormData] = useState<EnrollCourseFormData>({
    gradeId: '',
    term: '',
    courseId: ''
  });
  const [availableCourses, setAvailableCourses] = useState<CourseOption[]>([]);
  const [isLoadingCourseOptions, setIsLoadingCourseOptions] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isEnrollingAll, setIsEnrollingAll] = useState(false);
  const [showEnrollAllConfirmModal, setShowEnrollAllConfirmModal] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollGrades, setEnrollGrades] = useState<GradeOption[]>([]);
  const [loadingEnrollGrades, setLoadingEnrollGrades] = useState(false);
  const [enrollGradesError, setEnrollGradesError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc'
  });
  const studentDetailsLoaded = useRef(false);
  const enrolledCoursesLoaded = useRef(false);
  const progressLoaded = useRef(false);
  const [lessonsProgress, setLessonsProgress] = useState<VideoProgressDto | null>(null);
  const [quizzesProgress, setQuizzesProgress] = useState<QuizProgressDto | null>(null);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [isResettingDevice, setIsResettingDevice] = useState(false);
  const [resetDeviceError, setResetDeviceError] = useState<string | null>(null);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordMode, setResetPasswordMode] = useState<'generate' | 'custom'>('generate');
  const [customNewPassword, setCustomNewPassword] = useState('');
  const [customConfirmPassword, setCustomConfirmPassword] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [revealedNewPassword, setRevealedNewPassword] = useState<string | null>(null);

  useEffect(() => {
    console.log("StudentDetails component mounted with id:", id);
    if (!studentDetailsLoaded.current) {
      fetchStudentDetails();
      studentDetailsLoaded.current = true;
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const loadGrades = async () => {
      setLoadingGrades(true);
      setGradesError(null);
      try {
        const res = await authFetch(apiUrl('Grade'), {
          method: 'GET',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${authService.getToken()}`
          }
        });
        if (!res.ok) throw new Error('Failed to fetch grades');
        const json = await res.json();
        if (cancelled) return;
        if (json?.success && Array.isArray(json.data)) {
          const mapped: GradeOption[] = json.data.map((g: Record<string, unknown>) => ({
            id: String(g.id ?? g.Id ?? ''),
            name: String(g.name ?? g.Name ?? ''),
            code: String(g.code ?? g.Code ?? ''),
            level: typeof g.level === 'number' ? g.level : typeof g.Level === 'number' ? g.Level : undefined,
            active: (g.active ?? g.Active) as boolean | undefined
          }));
          setGrades(mapped);
          setEnrollGrades(mapped.filter((g) => Boolean(g.code)));
        } else {
          setGrades([]);
          setEnrollGrades([]);
        }
      } catch {
        if (!cancelled) {
          setGradesError('تعذر تحميل الصفوف');
          setGrades([]);
          setEnrollGrades([]);
        }
      } finally {
        if (!cancelled) setLoadingGrades(false);
      }
    };
    loadGrades();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!student || grades.length === 0) return;
    const gradeId = resolveGradeIdFromStudent(student, grades);
    if (gradeId) {
      setFormData((prev) => (prev.gradeId === gradeId ? prev : { ...prev, gradeId }));
    }
  }, [student, grades]);

  useEffect(() => {
    if (!showEnrollModal) return;
    setLoadingEnrollGrades(loadingGrades);
    setEnrollGradesError(gradesError);
  }, [showEnrollModal, loadingGrades, gradesError]);

  // Modify effect to fetch courses only once when tab changes to courses
  useEffect(() => {
    if (activeTab === 'courses' && !enrolledCoursesLoaded.current) {
      fetchEnrolledCourses();
      enrolledCoursesLoaded.current = true;
    }
  }, [activeTab, id]);

  // Remove this useEffect to prevent duplicate course fetching
  // useEffect(() => {
  //   if (activeTab === 'courses') {
  //     fetchEnrolledCourses();
  //   }
  // }, [currentPage, activeTab]);

  // Add new useEffect to only refetch when page changes (not on initial load)
  useEffect(() => {
    if (currentPage > 1 && activeTab === 'courses') {
      fetchEnrolledCourses();
    }
  }, [currentPage]);

  // Reset progress ref when student id changes so we refetch for new student
  useEffect(() => {
    progressLoaded.current = false;
    setLessonsProgress(null);
    setQuizzesProgress(null);
    setProgressError(null);
  }, [id]);

  useEffect(() => {
    if (activeTab === 'progress' && !progressLoaded.current && id) {
      fetchProgress();
      progressLoaded.current = true;
    }
  }, [activeTab, id]);

  const fetchProgress = async () => {
    if (!id) return;
    setIsLoadingProgress(true);
    setProgressError(null);
    try {
      const [lessonsRes, quizzesRes] = await Promise.all([
        authFetch(apiUrl(`Student/GetStudentLessonsProgress/${id}`), {
          method: 'GET',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${authService.getToken()}`
          }
        }),
        authFetch(apiUrl(`Student/GetStudentQuizzesProgress/${id}`), {
          method: 'GET',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${authService.getToken()}`
          }
        })
      ]);

      if (!lessonsRes.ok || !quizzesRes.ok) {
        const errRes = !lessonsRes.ok ? lessonsRes : quizzesRes;
        const errBody = await errRes.json().catch(() => ({}));
        throw new Error(errBody.message || `طلب فاشل (${errRes.status})`);
      }

      const lessonsJson = await lessonsRes.json();
      const quizzesJson = await quizzesRes.json();

      if (lessonsJson.success && lessonsJson.data) {
        const d = lessonsJson.data;
        setLessonsProgress({
          totalVideos: d.totalVideos ?? d.TotalVideos ?? 0,
          watchedVideos: d.watchedVideos ?? d.WatchedVideos ?? 0,
          progress: d.progress ?? d.Progress ?? 0
        });
      }
      if (quizzesJson.success && quizzesJson.data) {
        const d = quizzesJson.data;
        const results = (d.results ?? d.Results ?? []).map((r: any) => ({
          lessonId: r.lessonId ?? r.LessonId,
          lessonName: r.lessonName ?? r.LessonName ?? '',
          score: r.score ?? r.Score ?? 0,
          submittedAt: r.submittedAt ?? r.SubmittedAt ?? ''
        }));
        setQuizzesProgress({
          totalQuizzes: d.totalQuizzes ?? d.TotalQuizzes ?? 0,
          solvedQuizzes: d.solvedQuizzes ?? d.SolvedQuizzes ?? 0,
          results
        });
      }
      if (!lessonsJson.success && !quizzesJson.success) {
        setProgressError(lessonsJson.message || quizzesJson.message || 'فشل تحميل بيانات التقدم');
      }
    } catch (e) {
      console.error('Error fetching progress:', e);
      setProgressError('فشل تحميل بيانات التقدم. يرجى المحاولة لاحقاً.');
    } finally {
      setIsLoadingProgress(false);
    }
  };

  const fetchEnrolledCourses = async () => {
    if (!id) return;
    
    setIsLoadingCourses(true);
    setCoursesError(null);
    try {
      const response = await authFetch(
        apiUrl(`Student/Student-Enrolled-Courses?studentId=${id}&pagenumber=${currentPage}&pagesize=${pageSize}`),
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch enrolled courses');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setEnrolledCourses(result.data.courses);
        setTotalPages(result.data.totalPages);
        setTotalCount(result.data.totalCount);
      } else {
        throw new Error(result.message || 'Failed to fetch enrolled courses');
      }
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      setCoursesError('Failed to load enrolled courses. Please try again later.');
      setEnrolledCourses([]);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const fetchStudentDetails = async () => {
    console.log("Fetching student details for id:", id);
    setIsLoading(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl(`AdminStudent/${id}`), {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch student details');
      }

      const result = await response.json();
      console.log('API Response:', result); // Debug log
      
      if (result.success && result.data) {
        setStudent(result.data);
        setFormData({
          name: result.data.userName || '',
          gradeId: '',
          phonenumber: result.data.phonenumber || '',
          parentPhone: result.data.parentPhone || ''
        });
      } else {
        throw new Error(result.message || 'Invalid data format received from server');
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      setError('Failed to load student details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Update local form state
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const selectedGrade = grades.find((g) => g.id === formData.gradeId);
      const academicYear = selectedGrade?.level != null
        ? levelToAcademicYear[selectedGrade.level]
        : undefined;

      if (!academicYear) {
        setError('اختر صفًا دراسيًا صحيحًا.');
        setIsSaving(false);
        return;
      }

      const requestBody = {
        academicYear,
        userName: formData.name,
        phoneNumber: formData.phonenumber,
        parentPhone: formData.parentPhone,
        email: student?.email || '',
        active: true,
        emailConfirmed: student?.emailConfirmed ?? false
      };

      console.log('Saving with data:', requestBody); // Debug log

      const response = await authFetch(apiUrl(`AdminStudent/update/${id}`), {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      console.log('Update response:', result); // Debug log

      if (result.success) {
        setUpdateMessage('تم تحديث الطالب بنجاح');
        setError(null);
        // Refresh data from server
        fetchStudentDetails();
        
        // Auto-dismiss success message after 3 seconds
        setTimeout(() => {
          setUpdateMessage(null);
        }, 3000);
      } else {
        setError(result.message || 'Failed to update student');
        setUpdateMessage(null);
      }
    } catch (error) {
      console.error('Error updating student:', error);
      setError('Failed to update student. Please try again later.');
      setUpdateMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (student) {
      setFormData({
        name: student.userName || '',
        gradeId: resolveGradeIdFromStudent(student, grades),
        phonenumber: student.phonenumber || '',
        parentPhone: student.parentPhone || ''
      });
    }
  };

  const handleEnrollAllGradeCourses = async () => {
    if (!id) return;

    setIsEnrollingAll(true);
    setCoursesError(null);
    try {
      const response = await authFetch(
        apiUrl(`AdminStudent/enroll-all-grade-courses?studentId=${encodeURIComponent(id)}`),
        {
          method: 'PUT',
          headers: {
            accept: '*/*',
            Authorization: `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        setShowEnrollAllConfirmModal(false);
        enrolledCoursesLoaded.current = false;
        await fetchEnrolledCourses();
        enrolledCoursesLoaded.current = true;
        const detail = result.data;
        const enrolledCount = detail?.enrolledCount;
        const message =
          typeof enrolledCount === 'number'
            ? `تم التسجيل في ${enrolledCount} دورة`
            : result.message || 'تم تسجيل الطالب في دورات الصف بنجاح';
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 4000);
      } else {
        throw new Error(result.message || 'فشل تسجيل الطالب في دورات الصف');
      }
    } catch (err) {
      console.error('Error enrolling in all grade courses:', err);
      setCoursesError(
        err instanceof Error ? err.message : 'فشل تسجيل الطالب في دورات الصف. حاول مرة أخرى.'
      );
    } finally {
      setIsEnrollingAll(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleUnenroll = async (courseId: string) => {
    if (!id) return;
    
    setIsUnenrolling(true);
    try {
      const response = await authFetch(
        apiUrl(`AdminStudent/unenroll/${id}/${courseId}`),
        {
          method: 'PUT',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        // Remove the course from the local state
        setEnrolledCourses(prev => prev.filter(course => course.id !== courseId));
        setTotalCount(prev => prev - 1);
        setCourseToUnenroll(null);
        setSuccessMessage(result.data || 'تم إلغاء تسجيل الطالب بنجاح');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to unenroll student');
      }
    } catch (error) {
      console.error('Error unenrolling student:', error);
      setCoursesError('Failed to unenroll student. Please try again later.');
    } finally {
      setIsUnenrolling(false);
    }
  };

  const handleEnrollFormChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setEnrollFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      // If changing grade, reset term and courseId
      if (name === 'gradeId') {
        newData.term = '';
        newData.courseId = '';
      }
      
      // If changing term, only reset courseId
      if (name === 'term') {
        newData.courseId = '';
      }

      if (newData.gradeId && newData.term) {
        fetchAvailableCourses(newData.gradeId, newData.term);
      }

      return newData;
    });
  };

  const fetchAvailableCourses = async (gradeId: string, term: string) => {
    setIsLoadingCourseOptions(true);
    setEnrollError(null);
    try {
      const response = await authFetch(
        buildCoursesUrl(gradeId, term),
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch available courses');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setAvailableCourses(result.data.data.map((course: any) => ({
          id: course.id,
          name: course.courseName,
          term: course.term,
          description: course.description,
          price: course.price
        })));
      } else {
        throw new Error(result.message || 'Failed to fetch available courses');
      }
    } catch (error) {
      console.error('Error fetching available courses:', error);
      setEnrollError('Failed to load available courses. Please try again.');
      setAvailableCourses([]);
    } finally {
      setIsLoadingCourseOptions(false);
    }
  };

  const handleEnrollSubmit = async () => {
    if (!id || !enrollFormData.courseId) return;
    
    setIsEnrolling(true);
    setEnrollError(null);
    try {
      const response = await authFetch(
        apiUrl(`AdminStudent/enroll/${id}/${enrollFormData.courseId}`),
        {
          method: 'PUT',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        // Refresh enrolled courses list
        fetchEnrolledCourses();
        setShowEnrollModal(false);
        setSuccessMessage('تم تسجيل الطالب بنجاح');
        
        // Reset form data
        setEnrollFormData({
          gradeId: '',
          term: '',
          courseId: ''
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to enroll student');
      }
    } catch (error) {
      console.error('Error enrolling student:', error);
      setEnrollError('Failed to enroll student. Please try again.');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleSort = (key: keyof EnrolledCourse) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedCourses = () => {
    if (!sortConfig.key) return enrolledCourses;

    return [...enrolledCourses].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      // Handle null or undefined values
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (!bValue) return sortConfig.direction === 'asc' ? 1 : -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (key: keyof EnrolledCourse) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const handleResetDevices = async () => {
    if (!id) return;
    
    setIsResettingDevice(true);
    setResetDeviceError(null);
    try {
      const response = await authFetch(
        apiUrl(`Auth/reset-student-devices/${id}`),
        {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMessage(result.data || 'تم إعادة تعيين أجهزة الطالب بنجاح');
        setShowResetConfirmModal(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to reset student devices');
      }
    } catch (error) {
      console.error('Error resetting student devices:', error);
      setResetDeviceError(error instanceof Error ? error.message : 'Failed to reset student devices. Please try again later.');
    } finally {
      setIsResettingDevice(false);
    }
  };

  const handleResetPassword = async () => {
    if (!id) return;
    setResetPasswordError(null);
    if (resetPasswordMode === 'custom') {
      if (customNewPassword.length < 6) {
        setResetPasswordError('كلمة المرور يجب أن تكون 6 أحرف على الأقل.');
        return;
      }
      if (customNewPassword !== customConfirmPassword) {
        setResetPasswordError('كلمة المرور وتأكيدها غير متطابقتين.');
        return;
      }
    }
    setIsResettingPassword(true);
    try {
      const body: { studentId: string; newPassword?: string; forceLogout: boolean } = {
        studentId: id,
        forceLogout: true,
      };
      if (resetPasswordMode === 'custom' && customNewPassword) {
        body.newPassword = customNewPassword;
      }
      const response = await authFetch(apiUrl(`AdminStudent/reset-password/${id}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: '*/*',
          Authorization: `Bearer ${authService.getToken()}`,
        },
        body: JSON.stringify({
          StudentId: id,
          NewPassword: body.newPassword || undefined,
          ForceLogout: true,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'فشل إعادة تعيين كلمة المرور.');
      }
      const newPwd = (result.data?.newPassword ?? result.data?.NewPassword ?? (resetPasswordMode === 'custom' ? customNewPassword : null)) as string | null;
      setRevealedNewPassword(newPwd || null);
      setResetPasswordSuccess(true);
    } catch (err) {
      console.error('Reset password error:', err);
      setResetPasswordError(err instanceof Error ? err.message : 'فشل إعادة تعيين كلمة المرور.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setResetPasswordError(null);
    setResetPasswordSuccess(false);
    setRevealedNewPassword(null);
    setCustomNewPassword('');
    setCustomConfirmPassword('');
    setResetPasswordMode('generate');
  };

  const copyPasswordToClipboard = () => {
    if (!revealedNewPassword) return;
    navigator.clipboard.writeText(revealedNewPassword);
    setSuccessMessage('تم نسخ كلمة المرور.');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 rounded-lg shadow-md bg-white">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xl font-medium text-gray-700">جاري تحميل تفاصيل الطالب...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full p-8 rounded-lg shadow-md bg-white">
          <div className="text-red-500 text-center mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-center mb-4">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <div className="flex justify-center">
            <button 
              onClick={() => navigate('/students')} 
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              العودة إلى الطلاب
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full p-8 rounded-lg shadow-md bg-white text-center">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <h2 className="text-2xl font-bold mb-4">الطالب غير موجود</h2>
          <p className="text-gray-600 mb-6">الطالب الذي تبحث عنه غير موجود أو تم حذفه.</p>
          <button 
            onClick={() => navigate('/students')} 
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            العودة إلى الطلاب
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen" dir="rtl">
      {/* Top Navigation Bar */}
              <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center">
        <button
          onClick={() => navigate('/students')}
            className="ml-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">الملف الشخصي للطالب</h1>
            <p className="text-gray-500 text-sm">آخر تحديث: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <div className="mt-4 md:mt-0 flex items-center">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${student?.emailConfirmed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
            {student?.emailConfirmed ? 'البريد الإلكتروني مؤكد' : 'البريد الإلكتروني معلق'}
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 bg-white rounded-xl shadow-sm p-1">
        <div className="flex">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-3 px-4 text-center rounded-lg transition-colors ${
              activeTab === 'details'
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            تفاصيل الملف الشخصي
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`flex-1 py-3 px-4 text-center rounded-lg transition-colors ${
              activeTab === 'courses'
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            الباقات المسجلة
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`flex-1 py-3 px-4 text-center rounded-lg transition-colors ${
              activeTab === 'progress'
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            متابعة التقدم
          </button>
        </div>
      </div>

      {/* Notification Messages */}
      {updateMessage && (
        <div className="mb-6 transform transition-all duration-300 ease-in-out">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{updateMessage}</p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button 
                    onClick={() => setUpdateMessage(null)}
                    className="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button 
                    onClick={() => setError(null)}
                    className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeTab === 'details' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Student Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-indigo-100 text-indigo-500 text-2xl font-bold mb-4">
                  {formData.name.substring(0, 2).toUpperCase()}
                </div>
                <h2 className="text-xl font-semibold">{formData.name}</h2>
                <p className="text-gray-500">
                  {grades.find((g) => g.id === formData.gradeId)?.name ?? student?.gradeName ?? '–'}
                </p>
              </div>
              
              <div className="border-t border-gray-100 pt-4">
                              <div className="flex items-center py-2">
                <svg className="w-5 h-5 text-gray-400 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-600">{student?.email}</span>
              </div>
              <div className="flex items-center py-2">
                <svg className="w-5 h-5 text-gray-400 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-gray-600">{student?.phonenumber}</span>
              </div>
              <div className="flex items-center py-2">
                <svg className="w-5 h-5 text-gray-400 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-gray-600">{student?.parentPhone}</span>
              </div>
              {(student?.governorate || student?.center) && (
                <>
                  {student?.governorate && (
                    <div className="flex items-center py-2">
                      <svg className="w-5 h-5 text-gray-400 ml-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-600">المحافظة: {student.governorate}</span>
                    </div>
                  )}
                  {student?.center && (
                    <div className="flex items-center py-2">
                      <svg className="w-5 h-5 text-gray-400 ml-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="text-gray-600">المركز: {student.center}</span>
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center py-2">
                <svg className="w-5 h-5 text-gray-400 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-600">
                  {new Date(student?.registrationDate || '').toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="flex items-center py-2">
                <svg className="w-5 h-5 text-gray-400 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                </svg>
                <span className="text-gray-600">ID: {student?.id}</span>
              </div>
              
              <div className="flex items-center py-2">
                <svg className="w-5 h-5 text-gray-400 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-600">بطاقة الطالب</span>
              </div>
              <div className="flex justify-center py-2">
                {student?.imagePath ? (
                  <img 
                    src={getImageUrl(student.imagePath) || student.imagePath} 
                    alt={`بطاقة الطالب ${student?.userName}`}
                    className="w-32 h-40 rounded-lg shadow-md object-cover"
                    onError={(e: any) => {
                      const imgElement = e.target as HTMLImageElement;
                      if (imgElement && !imgElement.dataset.fallbackLoaded) {
                        imgElement.dataset.fallbackLoaded = 'true';
                        // Use a simple data URL for fallback instead of external service
                        imgElement.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NzM4NyIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=';
                      }
                    }}
                  />
                ) : (
                  <div className="w-32 h-40 rounded-lg shadow-md bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-400 text-xs">لا توجد صورة</span>
                  </div>
                )}
              </div>
              </div>
              <div className="border-t border-gray-100 pt-4 mt-4 space-y-2">
                <button
                  onClick={() => setShowResetConfirmModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  إعادة تعيين وصول الأجهزة
                </button>
                <button
                  onClick={() => { setResetPasswordError(null); setResetPasswordSuccess(false); setRevealedNewPassword(null); setShowResetPasswordModal(true); }}
                  className="w-full px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                >
                  إعادة تعيين كلمة المرور
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Edit Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">تعديل معلومات الطالب</h3>
              
              <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                    الاسم الكامل
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="أدخل الاسم الكامل للطالب"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الصف الدراسي
            </label>
            <select
              name="gradeId"
              value={formData.gradeId}
              onChange={handleInputChange}
              disabled={loadingGrades}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white disabled:bg-gray-100"
              required
            >
              <option value="">{loadingGrades ? 'جاري تحميل الصفوف...' : 'اختر الصف'}</option>
              {grades.map((grade) => (
                <option key={grade.id} value={grade.id}>
                  {grade.name}
                </option>
              ))}
            </select>
            {gradesError && (
              <p className="mt-1 text-sm text-red-600">{gradesError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              هاتف الطالب
            </label>
            <input
              type="tel"
              name="phonenumber"
              value={formData.phonenumber}
              onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="أدخل رقم هاتف الطالب"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              هاتف ولى الأمر
            </label>
            <input
              type="tel"
              name="parentPhone"
              value={formData.parentPhone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="أدخل رقم هاتف ولى الأمر"
            />
          </div>

                <div className="flex justify-start space-x-reverse space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    إعادة تعيين التغييرات
                  </button>
            <button
              type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-3 bg-indigo-600 rounded-lg text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {isSaving ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        جاري الحفظ...
                      </span>
                    ) : (
                      'حفظ التغييرات'
                    )}
            </button>
          </div>
        </div>
      </div>
          </div>
        </div>
      ) : activeTab === 'courses' ? (
        /* Enrolled Courses Tab */
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="mt-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">الباقات المسجلة</h3>
                <p className="text-sm text-gray-500">
                  تم العثور على {totalCount} {totalCount === 1 ? 'دورة' : 'دورات'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowEnrollAllConfirmModal(true)}
                disabled={isEnrollingAll}
                className="w-full sm:w-auto shrink-0 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-center disabled:opacity-50"
              >
                {isEnrollingAll ? 'جاري التسجيل...' : 'تسجيل الطالب فى كل المواد'}
              </button>
            </div>

            {isLoadingCourses ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : coursesError ? (
              <div className="text-center py-8">
                <p className="text-red-500">{coursesError}</p>
              </div>
            ) : enrolledCourses.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">لم يتم العثور على دورات</p>
              </div>
            ) : (
              <>
                <div className="md:hidden space-y-3">
                  {getSortedCourses().map((course) => (
                    <div
                      key={course.id}
                      className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 shadow-sm space-y-3"
                    >
                      <div
                        className="text-base font-medium text-indigo-600 hover:text-indigo-900 cursor-pointer"
                        onClick={() => navigate(`/courses/${course.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') navigate(`/courses/${course.id}`);
                        }}
                      >
                        {course.courseName}
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed break-words" title={course.description}>
                        {truncateText(course.description, ENROLLED_COURSE_DESCRIPTION_MAX)}
                      </p>
                      <p className="text-sm text-gray-500">
                        تاريخ التسجيل:{' '}
                        {course.enrollmentDate
                          ? new Date(course.enrollmentDate).toLocaleDateString('ar-EG', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : '-'}
                      </p>
                      <button
                        type="button"
                        onClick={() => setCourseToUnenroll(course)}
                        className="w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        إلغاء التسجيل
                      </button>
                    </div>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('courseName')}
                        >
                          <div className="flex items-center space-x-reverse space-x-1">
                            <span>اسم الدورة</span>
                            {getSortIcon('courseName')}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('description')}
                        >
                          <div className="flex items-center space-x-reverse space-x-1">
                            <span>الوصف</span>
                            {getSortIcon('description')}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('enrollmentDate')}
                        >
                          <div className="flex items-center space-x-reverse space-x-1">
                            <span>تاريخ التسجيل</span>
                            {getSortIcon('enrollmentDate')}
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          الإجراءات
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getSortedCourses().map((course) => (
                        <tr key={course.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div 
                              onClick={() => navigate(`/courses/${course.id}`)}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-900 cursor-pointer"
                            >
                              {course.courseName}
                            </div>
                          </td>
                          <td className="px-6 py-4 max-w-md">
                            <div
                              className="text-sm text-gray-900"
                              title={course.description}
                            >
                              {truncateText(course.description, ENROLLED_COURSE_DESCRIPTION_MAX)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {course.enrollmentDate ? new Date(course.enrollmentDate).toLocaleDateString('ar-EG', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                          <button
                                onClick={() => setCourseToUnenroll(course)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                              >
                                إلغاء التسجيل
                              </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${
                          currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                      >
                        السابق
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${
                          currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                      >
                        التالي
                      </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          عرض <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> إلى{' '}
                          <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> من{' '}
                          <span className="font-medium">{totalCount}</span> نتيجة
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                              currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="sr-only">السابق</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.414L10 11.414l1.293 1.293a1 1 0 001.414 0l4.5-4.25a.75.75 0 000-1.06z" clipRule="evenodd" />
                            </svg>
                          </button>
                          
                          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            let pageNum = i + 1;
                            if (totalPages > 5) {
                              if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                  currentPage === pageNum
                                    ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                              currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="sr-only">التالي</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        /* Progress Tab */
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">متابعة التقدم</h3>
          {isLoadingProgress ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            </div>
          ) : progressError ? (
            <div className="text-center py-8">
              <p className="text-red-500">{progressError}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Lessons (Videos) Progress */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-base font-medium text-gray-700 mb-3">تقدم الدروس (الفيديوهات)</h4>
                {lessonsProgress ? (
                  lessonsProgress.totalVideos === 0 ? (
                    <p className="text-gray-500 text-sm">لا توجد فيديوهات في الدورات المسجلة.</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>تمت مشاهدته: {lessonsProgress.watchedVideos} من {lessonsProgress.totalVideos}</span>
                        <span className="font-medium text-indigo-600">{lessonsProgress.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-indigo-600 h-2.5 rounded-full transition-all"
                          style={{ width: `${Math.min(100, lessonsProgress.progress)}%` }}
                        />
                      </div>
                    </div>
                  )
                ) : (
                  <p className="text-gray-500 text-sm">لا توجد فيديوهات في الدورات المسجلة.</p>
                )}
              </div>

              {/* Quizzes Progress */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-base font-medium text-gray-700 mb-3">تقدم الاختبارات</h4>
                {quizzesProgress ? (
                  quizzesProgress.totalQuizzes === 0 ? (
                    <p className="text-gray-500 text-sm">لا توجد اختبارات في الدورات المسجلة.</p>
                  ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>تم حله: {quizzesProgress.solvedQuizzes} من {quizzesProgress.totalQuizzes}</span>
                    </div>
                    {quizzesProgress.results && quizzesProgress.results.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">الاختبار</th>
                              <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">الدرجة</th>
                              <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">تاريخ الإرسال</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {quizzesProgress.results.map((r) => (
                              <tr key={r.lessonId} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">{r.lessonName}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{r.score}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                  {new Date(r.submittedAt).toLocaleDateString('ar-EG', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">لم يحل الطالب أي اختبارات بعد.</p>
                    )}
                  </div>
                  )
                ) : (
                  <p className="text-gray-500 text-sm">لا توجد اختبارات في الدورات المسجلة.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enroll all grade courses confirmation */}
      {showEnrollAllConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
                <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">تسجيل الطالب فى كل المواد</h3>
              <p className="text-sm text-gray-500 mb-6">
                تسجيل الطالب في جميع دورات صفه النشطة؟
                {(grades.find((g) => g.id === formData.gradeId)?.name ?? student?.gradeName) && (
                  <>
                    <br />
                    <span className="mt-2 inline-block text-gray-700">
                      الصف:{' '}
                      <span className="font-semibold">
                        {grades.find((g) => g.id === formData.gradeId)?.name ?? student?.gradeName}
                      </span>
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex justify-start space-x-reverse space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setShowEnrollAllConfirmModal(false)}
                disabled={isEnrollingAll}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                onClick={handleEnrollAllGradeCourses}
                disabled={isEnrollingAll}
              >
                {isEnrollingAll ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري التسجيل...
                  </span>
                ) : (
                  'تأكيد التسجيل'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unenroll Confirmation Modal */}
      {courseToUnenroll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">إلغاء تسجيل الطالب</h3>
              <p className="text-sm text-gray-500 mb-6">
                هل أنت متأكد أنك تريد إلغاء تسجيل هذا الطالب من <span className="font-semibold text-gray-700">{courseToUnenroll.courseName}</span>؟
                لا يمكن التراجع عن هذا الإجراء.
              </p>
            </div>
            <div className="flex justify-start space-x-reverse space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setCourseToUnenroll(null)}
                disabled={isUnenrolling}
              >
                إلغاء
              </button>
              <button
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                onClick={() => courseToUnenroll && handleUnenroll(courseToUnenroll.id)}
                disabled={isUnenrolling}
              >
                {isUnenrolling ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري إلغاء التسجيل...
                  </span>
                ) : 'تأكيد إلغاء التسجيل'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Course Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">التسجيل في دورة</h3>
              <button
                onClick={() => setShowEnrollModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {enrollError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                {enrollError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الصف
                </label>
                <select
                  name="gradeId"
                  value={enrollFormData.gradeId}
                  onChange={handleEnrollFormChange}
                  disabled={loadingEnrollGrades}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">اختر الصف</option>
                  {enrollGrades
                    .filter(g => g.active !== false)
                    .map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name || g.code}
                      </option>
                    ))}
                </select>
                {enrollGradesError && (
                  <p className="mt-1 text-sm text-red-600">{enrollGradesError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الفصل
                </label>
                <select
                  name="term"
                  value={enrollFormData.term}
                  onChange={handleEnrollFormChange}
                  disabled={!enrollFormData.gradeId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">اختر الفصل</option>
                  <option value="First">الأول</option>
                  <option value="Second">الثاني</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الدورة
                </label>
                <select
                  name="courseId"
                  value={enrollFormData.courseId}
                  onChange={handleEnrollFormChange}
                  disabled={!enrollFormData.gradeId || !enrollFormData.term || isLoadingCourseOptions}
                  className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 text-sm"
                >
                  <option value="">
                    {isLoadingCourseOptions 
                      ? 'جاري تحميل الباقات...' 
                      : !enrollFormData.gradeId
                        ? 'اختر الصف أولاً'
                        : !enrollFormData.term
                        ? 'اختر الفصل أولاً'
                        : 'اختر الدورة'
                    }
                  </option>
                  {availableCourses.map(course => (
                    <option key={course.id} value={course.id}>
                      {truncateText(course.name, 50)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-start space-x-reverse space-x-3">
              <button
                onClick={() => setShowEnrollModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleEnrollSubmit}
                disabled={!enrollFormData.courseId || isEnrolling}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isEnrolling ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري التسجيل...
                  </span>
                ) : 'تسجيل'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Devices Confirmation Modal */}
      {showResetConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">إعادة تعيين وصول الأجهزة</h3>
              <p className="text-sm text-gray-500 mb-6">
                هل أنت متأكد أنك تريد إعادة تعيين وصول الأجهزة لهذا الطالب؟ سيتم تسجيل خروجه من جميع أجهزته.
              </p>
              {resetDeviceError && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  {resetDeviceError}
                </div>
              )}
            </div>
            <div className="flex justify-start space-x-reverse space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setShowResetConfirmModal(false)}
                disabled={isResettingDevice}
              >
                إلغاء
              </button>
              <button
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                onClick={handleResetDevices}
                disabled={isResettingDevice}
              >
                {isResettingDevice ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -mr-1 ml-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري إعادة التعيين...
                  </span>
                ) : 'تأكيد إعادة التعيين'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            {resetPasswordSuccess ? (
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">تم تعيين كلمة المرور بنجاح</h3>
                {revealedNewPassword && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">كلمة المرور الجديدة:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-gray-100 rounded-md text-sm font-mono break-all text-right">
                        {revealedNewPassword}
                      </code>
                      <button
                        type="button"
                        onClick={copyPasswordToClipboard}
                        className="px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
                      >
                        نسخ
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">انسخها وأعطِها للطالب. سيُعاد تشغيل الجلسات تلقائياً.</p>
                  </div>
                )}
                <button
                  onClick={closeResetPasswordModal}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
                >
                  إغلاق
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4 mx-auto">
                  <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">إعادة تعيين كلمة المرور</h3>
                <p className="text-sm text-gray-500 mb-4 text-center">
                  اختر توليد كلمة مرور جديدة تلقائياً أو تعيين كلمة مرور محددة. سيتم تسجيل خروج الطالب من جميع الأجهزة.
                </p>
                {resetPasswordError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{resetPasswordError}</div>
                )}
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => { setResetPasswordMode('generate'); setResetPasswordError(null); }}
                    className={`flex-1 py-2 rounded-md text-sm font-medium ${resetPasswordMode === 'generate' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    توليد تلقائي
                  </button>
                  <button
                    type="button"
                    onClick={() => { setResetPasswordMode('custom'); setResetPasswordError(null); }}
                    className={`flex-1 py-2 rounded-md text-sm font-medium ${resetPasswordMode === 'custom' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    تعيين يدوي
                  </button>
                </div>
                {resetPasswordMode === 'custom' && (
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور الجديدة</label>
                      <input
                        type="password"
                        value={customNewPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomNewPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="6 أحرف على الأقل"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">تأكيد كلمة المرور</label>
                      <input
                        type="password"
                        value={customConfirmPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="أعد إدخال كلمة المرور"
                        dir="ltr"
                      />
                    </div>
                  </div>
                )}
                <div className="flex justify-start gap-3">
                  <button
                    type="button"
                    onClick={closeResetPasswordModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    disabled={isResettingPassword}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={isResettingPassword}
                    className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
                  >
                    {isResettingPassword ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        جاري التعيين...
                      </span>
                    ) : resetPasswordMode === 'generate' ? 'توليد كلمة مرور جديدة' : 'تعيين كلمة المرور'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetails; 